import moment from "moment";
import Api from "../Api";
import type { Metrics, SyncExecution, SyncStatus } from "../../components/config/interfaces/GetSyncExecutions";

/** Client for SM-API's unified sync surface (`/api/syncs`), backed by sync-api v2 */

/** Discriminator for a sync configuration */
export enum SyncType {
  Google = "GOOGLE",
  Azure = "AZURE",
  Clever = "CLEVER",
  ConnectId = "CONNECTID",
  OneRoster = "ONEROSTER",
  ClassLink = "CLASSLINK",
  PowerSchool = "POWERSCHOOL",
  Wonde = "WONDE",
  Ldap = "LDAP",
  Ldaps = "LDAPS",
}

/** The four sync types the SIS Integration page manages. */
export const SIS_SYNC_TYPES: SyncType[] = [SyncType.OneRoster, SyncType.ClassLink, SyncType.PowerSchool, SyncType.Wonde];

/** Credential status — secrets themselves are never returned, only their state. */
export enum CredentialState {
  NotSet = "NOT_SET",
  Set = "SET",
  Expired = "EXPIRED",
  Revoked = "REVOKED",
}

export interface CredentialStatus {
  kind: string;
  state: CredentialState;
  hint?: string | null;
}

/** Derived outcome of the most recent execution. */
export interface SyncSummary {
  state: string; // NEVER_RUN | RUNNING | SUCCEEDED | PARTIAL | FAILED | UNKNOWN
  message?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  isRunning: boolean;
  canExecute: boolean;
  blockedReason?: string | null;
  retryAfter?: string | null;
}

export interface YearAttributeMapping {
  enumValue: string;
  customerValue: string;
}

export interface YearAttributeConfig {
  enabled: boolean;
  mappings: YearAttributeMapping[];
}

export interface MergeMethod {
  __typename?: string;
  stripped?: boolean; // EmailMergeMethod
  expression?: string; // ComputedMergeMethod
}

export interface MergeConfigNode {
  priority: number;
  attachOnly: boolean;
  method?: MergeMethod;
}

/** OneRoster / ClassLink / PowerSchool provider config (union member). */
export interface OneRosterProviderConfig {
  __typename: "OnerosterSyncConfig";
  variant: string; // ONEROSTER | CLASSLINK | POWERSCHOOL
  integrationType: string;
  endpointUrl: string;
  tokenUrl?: string | null;
  clientId: string;
  syncClassrooms: boolean;
  syncParents: boolean;
  onerosterSyncParentTypes: string[];
  respectEnrolmentDate: boolean;
  respectAcademicSessions: boolean;
  compatibilityMode: boolean;
  yearAttributes: YearAttributeConfig;
  excludedSchools: string[];
  credential: CredentialStatus;
}

/** Wonde provider config (union member). */
export interface WondeProviderConfig {
  __typename: "WondeSyncConfig";
  wondeDistrictID: string;
  domain: string;
  studentEmailField: string;
  studentUsernameField: string;
  classNameFormat: string;
  teacherUsernameField: string;
  teacherEmailField: string;
  guardianEmailField: string;
  guardianRoles: string[];
  syncClassrooms: boolean;
  syncParents: boolean;
  yearAttributes: YearAttributeConfig;
  credential: CredentialStatus;
}

/** A toggled list of strings (e.g. domain filter / strip domain). */
export interface ToggledStringList {
  enabled: boolean;
  values: string[];
}

/** A single toggled string (e.g. Google OU filter). */
export interface ToggledString {
  enabled: boolean;
  value?: string | null;
}

/** Google provider config (union member). */
export interface GoogleProviderConfig {
  __typename: "GoogleSyncConfig";
  adminEmail: string;
  syncOrgUnits: boolean;
  syncClassrooms: boolean;
  syncGroupsByID: boolean;
  domainFilter: ToggledStringList;
  stripDomain: ToggledStringList;
  orgUnitFilter: ToggledString;
  ignoreEmptyGroups: boolean;
  ignoreEmptyClassrooms: boolean;
  credential: CredentialStatus;
}

/** Azure (Entra) provider config (union member). */
export interface AzureProviderConfig {
  __typename: "AzureSyncConfig";
  syncHiddenGroupMemberships: boolean;
  groupNameField: string; // DISPLAY_NAME | DESCRIPTION
  stripDomain: ToggledStringList;
  domainFilter: ToggledStringList;
  ignoreEmptyGroups: boolean;
  credential: CredentialStatus;
}

/** How Clever users are matched against existing ones. */
export type CleverMatchUsersBy = "EMAIL_ONLY" | "NAME_OR_EMAIL" | "USERNAME_OR_EMAIL";

/** A Clever contact relationship. */
export type CleverParentType = "PARENT_GUARDIAN" | "FAMILY" | "EMERGENCY" | "OTHER";

/**
 * Clever provider config (union member). The read query aliases `districtID` ->
 * `cleverDistrictID` and `syncParentTypes` -> `cleverSyncParentTypes` (to avoid
 * collisions with other providers in SM-API's shared result type), so those are
 * the JSON keys served here. `districtID` is set by the OAuth flow, not editable.
 */
export interface CleverProviderConfig {
  __typename: "CleverSyncConfig";
  cleverDistrictID?: string | null;
  matchUsersBy: CleverMatchUsersBy;
  syncClassroomData: boolean;
  forceUniqueClassroomNames: boolean;
  syncParents: boolean;
  cleverSyncParentTypes: CleverParentType[];
  yearAttributes: YearAttributeConfig;
  credential: CredentialStatus;
}

export type LdapServerType = "ACTIVE_DIRECTORY" | "OPEN_LDAP" | "NOVELL_EDIRECTORY" | "CUSTOM";
export type LdapProtocol = "LDAP" | "LDAPS";

/** The attribute set an LDAP server syncs with. Always resolved; editable only for CUSTOM. */
export interface LdapAttributeMap {
  username: string;
  manager: string;
  email: string;
  userBase: string;
  userGroup: string;
  userDN: string;
  groupBase: string;
  groupName: string;
  groupMemberName: string;
  groupDN: string;
  ouDN: string;
  ouName: string;
  userOwnedGroupMembership: boolean;
  authenticateWithSuppliedDN: boolean;
}

/** One LDAP server (an `ldap2` entry). The bind password is write-only; see `credential`. */
export interface LdapServerProviderConfig {
  __typename: "LdapServerSyncConfig";
  serverType: LdapServerType;
  protocol: LdapProtocol;
  hostname: string;
  port: number;
  baseDN: string;
  bindUsername: string;
  searchQuery: string;
  syncEnabled: boolean;
  syncOrgUnits: boolean;
  syncByDN: boolean;
  attributes: LdapAttributeMap;
  attributesEditable: boolean;
  credential: CredentialStatus;
}

/** The appliance's single legacy LDAP configuration. Same as a server minus `protocol`. */
export interface LegacyLdapProviderConfig {
  __typename: "LegacyLdapSyncConfig";
  serverType: LdapServerType;
  hostname: string;
  port: number;
  baseDN: string;
  bindUsername: string;
  searchQuery: string;
  syncEnabled: boolean;
  syncOrgUnits: boolean;
  syncByDN: boolean;
  attributes: LdapAttributeMap;
  attributesEditable: boolean;
  credential: CredentialStatus;
}

/**
 * The provider-specific config union. Discriminated by `__typename`.
 */
export type SyncProviderConfig =
  | OneRosterProviderConfig
  | WondeProviderConfig
  | GoogleProviderConfig
  | AzureProviderConfig
  | CleverProviderConfig
  | LdapServerProviderConfig
  | LegacyLdapProviderConfig
  | { __typename: string };

/** One sync configuration. */
export interface SyncConfigurationNode {
  id: string;
  applianceID: string;
  syncType: SyncType;
  name: string;
  enabled: boolean;
  readError?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  merge?: MergeConfigNode | null;
  config?: SyncProviderConfig | null;
  sync: SyncSummary;
}

export interface PageInfo {
  hasNextPage: boolean;
  endCursor?: string | null;
}

export interface SyncConfigurationEdge {
  cursor?: string;
  node: SyncConfigurationNode;
}

export interface SyncConfigurationConnection {
  edges: SyncConfigurationEdge[];
  pageInfo: PageInfo;
}

/**
 * List sync configurations for the currently selected device, optionally
 * filtered by sync type. Returns the raw connection (edges/pageInfo); callers
 * map `edges[].node` into their own view model.
 */
export async function listSyncConfigurations(syncTypes: SyncType[] = []): Promise<SyncConfigurationConnection> {
  const typeParams = syncTypes.map((t) => `syncType=${encodeURIComponent(t)}`);
  const edges: SyncConfigurationEdge[] = [];
  let pageInfo: PageInfo = { hasNextPage: false };
  let after: string | undefined;

  // Follow the cursor until every page is fetched. The page cap is a safety net
  // against a server that keeps reporting hasNextPage with a repeating cursor.
  for (let page = 0; page < 100; page++) {
    const query = [...typeParams, ...(after ? [`after=${encodeURIComponent(after)}`] : [])].join("&");
    let conn: SyncConfigurationConnection;
    try {
      conn = (await Api.getAsync(query ? `/api/syncs?${query}` : "/api/syncs")) as SyncConfigurationConnection;
    } catch (e) {
      // A first-page failure is a real error; a later page failing shouldn't hide
      // the rows we already have — return the partial result instead.
      if (page === 0) throw e;
      break;
    }
    edges.push(...(conn.edges ?? [])); // Go marshals an empty slice as null
    pageInfo = conn.pageInfo ?? { hasNextPage: false };
    after = pageInfo.hasNextPage ? pageInfo.endCursor ?? undefined : undefined;
    if (!after) break;
  }

  return { edges, pageInfo };
}

/** A field-level validation error returned by a sync mutation. */
export interface SyncFieldError {
  path: string[];
  code: string;
  message: string;
}

/** Write-only secret. Omit to leave unchanged; `set` to replace; `clear` to remove. */
export interface SecretInput {
  set?: string;
  clear?: boolean;
}

const MASKED_SECRET_PREFIX = "*****";

/**
 * The masked display of a stored secret: the prefix plus `credential.hint` (its
 * last four characters). Undefined when no credential is set.
 */
export const maskedSecret = (cred?: CredentialStatus): string | undefined =>
  cred?.state === CredentialState.Set ? `${MASKED_SECRET_PREFIX}${cred.hint ?? ""}` : undefined;

export type SecretMapper = (value?: string) => SecretInput | undefined;

/** On create there is no stored secret, so any non-empty value is the new secret. */
export const newSecret: SecretMapper = (value) => (value ? { set: value } : undefined);

/**
 * On update the masked placeholder means unchanged, so it is omitted along with
 * empty. Clearing is not exposed by the forms.
 */
export const changedSecret: SecretMapper = (value) => {
  if (value === undefined || value === "" || value.startsWith(MASKED_SECRET_PREFIX)) return undefined;
  return { set: value };
};

export interface MergeMethodInput {
  email?: { stripped: boolean };
  computed?: { expression: string };
}

export interface MergeConfigInput {
  priority: number;
  attachOnly: boolean;
  method: MergeMethodInput;
}

export interface YearAttributeMappingInput {
  enumValue: string;
  customerValue: string;
}

export interface YearAttributeConfigInput {
  enabled: boolean;
  mappings: YearAttributeMappingInput[];
}

export interface OneRosterSyncConfigInput {
  variant?: string;
  integrationType?: string;
  endpointUrl?: string;
  tokenUrl?: string;
  clientId?: string;
  clientSecret?: SecretInput;
  syncClassrooms?: boolean;
  syncParents?: boolean;
  syncParentTypes?: string[];
  respectEnrolmentDate?: boolean;
  respectAcademicSessions?: boolean;
  compatibilityMode?: boolean;
  yearAttributes?: YearAttributeConfigInput;
  excludedSchools?: string[];
}

export interface WondeSyncConfigInput {
  districtID?: string;
  domain?: string;
  accessToken?: SecretInput;
  studentEmailField?: string;
  studentUsernameField?: string;
  classNameFormat?: string;
  teacherUsernameField?: string;
  teacherEmailField?: string;
  guardianEmailField?: string;
  guardianRoles?: string[];
  syncClassrooms?: boolean;
  syncParents?: boolean;
  yearAttributes?: YearAttributeConfigInput;
}

export interface ToggledStringListInput {
  enabled: boolean;
  values: string[];
}

export interface ToggledStringInput {
  enabled: boolean;
  value?: string;
}

export interface GoogleSyncConfigInput {
  adminEmail?: string;
  syncOrgUnits?: boolean;
  syncClassrooms?: boolean;
  syncGroupsByID?: boolean;
  domainFilter?: ToggledStringListInput;
  stripDomain?: ToggledStringListInput;
  orgUnitFilter?: ToggledStringInput;
  ignoreEmptyGroups?: boolean;
  ignoreEmptyClassrooms?: boolean;
}

export interface AzureSyncConfigInput {
  syncHiddenGroupMemberships?: boolean;
  groupNameField?: string; // DISPLAY_NAME | DESCRIPTION
  stripDomain?: ToggledStringListInput;
  domainFilter?: ToggledStringListInput;
  ignoreEmptyGroups?: boolean;
}

/** Clever write block. `districtID` is omitted — it is written by the OAuth flow. */
export interface CleverSyncConfigInput {
  matchUsersBy?: CleverMatchUsersBy;
  syncClassroomData?: boolean;
  forceUniqueClassroomNames?: boolean;
  syncParents?: boolean;
  syncParentTypes?: CleverParentType[];
  yearAttributes?: YearAttributeConfigInput;
}

/** Replaces the whole attribute set. Only meaningful when the server type is CUSTOM. */
export type LdapAttributeMapInput = LdapAttributeMap;

export interface LdapServerSyncConfigInput {
  serverType?: LdapServerType;
  protocol?: LdapProtocol;
  hostname?: string;
  port?: number;
  baseDN?: string;
  bindUsername?: string;
  bindPassword?: SecretInput;
  searchQuery?: string;
  syncEnabled?: boolean;
  syncOrgUnits?: boolean;
  syncByDN?: boolean;
  attributes?: LdapAttributeMapInput;
}

export type LegacyLdapSyncConfigInput = Omit<LdapServerSyncConfigInput, "protocol">;

export interface CreateSyncConfigurationInput {
  syncType: SyncType;
  name: string;
  enabled: boolean;
  merge: MergeConfigInput;
  oneroster?: OneRosterSyncConfigInput;
  wonde?: WondeSyncConfigInput;
  google?: GoogleSyncConfigInput;
  azure?: AzureSyncConfigInput;
  clever?: CleverSyncConfigInput;
  ldapServer?: LdapServerSyncConfigInput;
  legacyLdap?: LegacyLdapSyncConfigInput;
}

export interface UpdateSyncConfigurationInput {
  name?: string;
  enabled?: boolean;
  merge?: MergeConfigInput;
  oneroster?: OneRosterSyncConfigInput;
  wonde?: WondeSyncConfigInput;
  google?: GoogleSyncConfigInput;
  azure?: AzureSyncConfigInput;
  clever?: CleverSyncConfigInput;
  ldapServer?: LdapServerSyncConfigInput;
  legacyLdap?: LegacyLdapSyncConfigInput;
}

/** Create a sync configuration. Rejects (HTTP 422) with a SyncFieldError list on domain errors. */
export async function createSyncConfiguration(input: CreateSyncConfigurationInput): Promise<void> {
  await Api.postAsync("/api/syncs", input);
}

/** Update a sync configuration (partial). Rejects (HTTP 422) with a SyncFieldError list on domain errors. */
export async function updateSyncConfiguration(id: string, input: UpdateSyncConfigurationInput): Promise<void> {
  await Api.patchAsync(`/api/syncs/${encodeURIComponent(id)}`, input);
}

/** Return the first sync configuration node of a given type (the singleton for Google/Azure), or undefined. */
export async function getSyncByType(syncType: SyncType): Promise<SyncConfigurationNode | undefined> {
  const conn = await listSyncConfigurations([syncType]);
  return (conn.edges ?? [])[0]?.node;
}

/** Resolve the sync configuration id for a singleton provider (Google/Azure), or undefined if none exists yet. */
export async function resolveSyncIdByType(syncType: SyncType): Promise<string | undefined> {
  return (await getSyncByType(syncType))?.id;
}

/** Revoke the stored credential for a sync (DELETE /api/syncs/{id}/credential). */
export async function revokeCredential(syncConfigurationId: string): Promise<void> {
  await Api.deleteAsync(`/api/syncs/${encodeURIComponent(syncConfigurationId)}/credential`, null);
}

export interface StartOAuthAuthorization {
  id: string;
  state: string;
  provider: SyncType;
  authorizationURL: string;
  requestedScopes: string[];
  expiresAt: string;
}

/**
 * Begin an OAuth authorization. `redirectURI` must be on sync-api's allow-list.
 * Returns the provider authorization URL to redirect to and a single-use `state`
 * the caller must persist and check on the callback. Rejects (422) on domain errors.
 */
export async function startOAuth(syncConfigurationId: string, redirectURI: string, loginHint?: string): Promise<StartOAuthAuthorization> {
  const body = loginHint ? { redirectURI, loginHint } : { redirectURI };
  const res = (await Api.postAsync(`/api/syncs/${encodeURIComponent(syncConfigurationId)}/oauth/start`, body)) as {
    authorization: StartOAuthAuthorization;
  };
  return res.authorization;
}

export interface CompleteOAuthAuthorization {
  syncConfigurationID: string;
  provider: SyncType;
  subject?: string | null;
  tenant?: string | null;
  grantedScopes: string[];
}

/**
 * Complete an OAuth authorization. `state` (from start) carries the appliance +
 * config server-side, so no id is needed here. Rejects (422) on domain errors.
 */
export async function completeOAuth(state: string, code: string): Promise<CompleteOAuthAuthorization> {
  const res = (await Api.postAsync("/api/syncs/oauth/complete", { state, code })) as { authorization: CompleteOAuthAuthorization };
  return res.authorization;
}

/** Best-effort human-readable message from a rejected sync mutation (jqXHR). */
export function extractSyncError(e: unknown, fallback: string): string {
  const body = (e as { responseJSON?: { errors?: SyncFieldError[]; error?: string } } | undefined)?.responseJSON;
  return body?.errors?.[0]?.message || body?.error || fallback;
}

/**
 * Trigger a sync run. SM-API rejects with HTTP 422 (a SyncFieldError list) when the
 * run is refused (e.g. disabled or missing credential), which surfaces as a rejected
 * promise; a 2xx resolve means the run was accepted.
 */
export async function executeSync(syncConfigurationId: string): Promise<void> {
  await Api.postAsync(`/api/syncs/${encodeURIComponent(syncConfigurationId)}/execute`, {});
}

/** A school returned by a live OneRoster-family provider lookup. */
export interface OneRosterSchool {
  id: string;
  name: string;
}

/**
 * Live lookup of the schools a OneRoster-family sync can see, used to pick which to
 * exclude. SM-API returns a bare array (the Flask endpoint wrapped it in `{schools}`).
 */
export async function listOneRosterSchools(syncConfigurationId: string): Promise<OneRosterSchool[]> {
  return (await Api.getAsync(`/api/syncs/${encodeURIComponent(syncConfigurationId)}/oneroster-schools`)) as OneRosterSchool[];
}

/** Delete a sync configuration. */
export async function deleteSyncConfiguration(syncConfigurationId: string): Promise<void> {
  await Api.deleteAsync(`/api/syncs/${encodeURIComponent(syncConfigurationId)}`, null);
}

/** Enable or disable a sync via SM-API's dedicated endpoint (cheaper than a full update). */
export async function setSyncEnabled(syncConfigurationId: string, enabled: boolean): Promise<void> {
  await Api.patchAsync(`/api/syncs/${encodeURIComponent(syncConfigurationId)}/enabled`, { enabled });
}

/** One recorded status within an execution. `createdAt` is RFC3339. */
export interface SyncStatusNode {
  id: string;
  status: string;
  level: string;
  message: string;
  createdAt: string;
  metrics?: Record<string, number> | null;
}

/** One execution of a sync configuration. SM-API uses `statuses` (not `syncStatuses`). */
export interface SyncExecutionNode {
  id: string;
  syncConfigurationID: string;
  createdAt: string;
  latestStatus?: SyncStatusNode | null;
  statuses: SyncStatusNode[];
}

export interface SyncExecutionEdge {
  cursor?: string;
  node: SyncExecutionNode;
}

export interface SyncExecutionsConnection {
  edges: SyncExecutionEdge[];
  pageInfo: PageInfo;
}

/** List past executions for one sync configuration (most recent first). */
export async function listSyncExecutions(syncConfigurationId: string): Promise<SyncExecutionsConnection> {
  return (await Api.getAsync(`/api/syncs/${encodeURIComponent(syncConfigurationId)}/executions`)) as SyncExecutionsConnection;
}

// SM-API returns timestamps as RFC3339; existing consumers parse the legacy
// api-dispatcher pattern, so reformat here to keep them working unchanged.
const LEGACY_TS_FORMAT = "YYYY-MM-DD HH:mm:ss.SSSSSS Z";

/** Map one SM-API status into the legacy SyncStatus shape (see mapExecutions). */
export function mapStatusNode(s: SyncStatusNode, executionID: string): SyncStatus {
  return {
    id: s.id,
    status: s.status as SyncStatus["status"],
    level: s.level,
    message: s.message,
    executionID,
    createdAt: moment.utc(s.createdAt).format(LEGACY_TS_FORMAT),
    applianceID: "",
    metrics: (s.metrics as Metrics | undefined) ?? undefined,
  };
}

/**
 * Map a SM-API executions connection into the SyncExecution[] shape existing
 * consumers use: `statuses` -> `syncStatuses`, executionID filled from the execution
 * id (SM-API statuses don't carry it), timestamps reformatted to the legacy pattern.
 */
export function mapExecutions(conn: SyncExecutionsConnection): SyncExecution[] {
  return (conn.edges ?? []).map((edge) => ({
    id: edge.node.id,
    applianceID: "",
    syncStatuses: (edge.node.statuses ?? []).map((s) => mapStatusNode(s, edge.node.id)),
  }));
}

/** One server's part in an LDAP run. A configured server absent from `members` did not sync. */
export interface LdapRunMember {
  syncConfigurationID: string;
  sync: SyncSummary;
}

/**
 * The appliance's latest LDAP run. Every LDAP server shares one run, so a server's
 * status only means anything relative to it. `statuses` is the run's own log,
 * newest first.
 */
export interface LdapRun {
  id: string;
  sync: SyncSummary;
  statuses: SyncStatusNode[];
  members: LdapRunMember[];
}

/** The latest LDAP run, or null when the appliance has never run one. */
export async function getLdapRun(): Promise<LdapRun | null> {
  return ((await Api.getAsync("/api/syncs/ldap/run")) as LdapRun | null) ?? null;
}

/**
 * Write the merge configuration every LDAP server on the appliance shares. Rejects
 * (HTTP 422) with a SyncFieldError list on domain errors, and 502 when the
 * appliance has no LDAP server to hold it.
 */
export async function updateLdapSharedMergeConfig(merge: MergeConfigInput): Promise<void> {
  await Api.putAsync("/api/syncs/ldap/merge", merge);
}
